import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Link2, CheckCircle, AlertCircle, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import * as settingsApi from '../endpoints';

export default function ApisTab({
  apiConnections,
  setApiConnections,
  updateApiKey
}) {
  const [saving, setSaving] = useState(false);

  const handleApiConnect = async (connectionId) => {
    const connection = apiConnections.find(c => c.id === connectionId);
    if (!connection.apiKey) {
      toast.error('Please enter an API key');
      return;
    }
    setSaving(true);
    try {
      await settingsApi.updateApiConnection(connectionId, connection.apiKey, 'connected');
      setApiConnections(prev => prev.map(c =>
        c.id === connectionId ? { ...c, status: 'connected' } : c
      ));
      toast.success(`${connection.name} connected successfully`);
    } catch (error) {
      toast.error(`Failed to connect ${connection.name}`);
    } finally {
      setSaving(false);
    }
  };

  const handleApiDisconnect = async (connectionId) => {
    setSaving(true);
    try {
      await settingsApi.updateApiConnection(connectionId, '', 'disconnected');
      setApiConnections(prev => prev.map(c =>
        c.id === connectionId ? { ...c, status: 'disconnected', apiKey: '' } : c
      ));
      toast.success('Connection removed');
    } catch (error) {
      toast.error('Failed to disconnect');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">External API Connections</CardTitle>
          <CardDescription>Connect to external services for enhanced risk assessment</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {apiConnections.map((connection) => (
            <div key={connection.id} className="p-4 rounded-xl border bg-card" data-testid={`api-connection-${connection.id}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Link2 className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-medium">{connection.name}</h4>
                    <p className="text-sm text-muted-foreground">{connection.provider}</p>
                  </div>
                </div>
                <Badge variant={connection.status === 'connected' ? 'default' : 'secondary'}>
                  {connection.status === 'connected' ? <><CheckCircle className="w-3 h-3 mr-1" /> Connected</> : <><AlertCircle className="w-3 h-3 mr-1" /> Disconnected</>}
                </Badge>
              </div>

              {connection.status === 'disconnected' ? (
                <div className="flex gap-3">
                  <Input
                    placeholder="Enter API Key"
                    type="password"
                    value={connection.apiKey}
                    onChange={(e) => updateApiKey(connection.id, e.target.value)}
                    className="flex-1"
                  />
                  <Button onClick={() => handleApiConnect(connection.id)} disabled={saving} className="bg-primary hover:bg-primary/90">
                    <Plus className="w-4 h-4 mr-2" />
                    Connect
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">API key configured and active</p>
                  <Button variant="outline" size="sm" onClick={() => handleApiDisconnect(connection.id)} className="text-destructive hover:text-destructive">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Disconnect
                  </Button>
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
